import React, { FC, PropsWithChildren, useMemo } from 'react';

import { Range } from 'slate';

// components
import VariationPlaceholder from './components/VariationPlaceholder';
import styled from 'styled-components';

import aampeTheme from '../../../../constants/aampeTheme';
//constants
import ICONS from '../../../../constants/icons';

import useComponentTypesQuery from '../../../../hooks/component-types/useComponentTypesQuery';

// models
import { ElementKind } from '../../../../models/editor.model';

import FormulaAlternateVariantDataView from '../../formulaAlternate/FormulaAlternateVariantDataView';
import { ToggleButtonValues } from '../FormulaEditorMain';
import VariantDeletePrompt from './VariantDeletePrompt';

interface StyledProps {
    isComponent: boolean;
    removeSpacing: boolean;
}
export interface AssistantStepsProps {
    formulaId?: number;
    selectedElement: { [key: string]: any };
    alternateCreateError: string | null;
    setAlternateCreateError: (value: string | null) => void;
    handleIconClick?: (event: any) => void;
    currentSelection: Range;
    setIsVariationTab: (value: ToggleButtonValues) => void;
    setSelectedElement: (value?: { [key: string]: any }) => void;
    removeSpacing?: boolean;
    hasWriteAccess: boolean;
}

const AssistantSteps: FC<PropsWithChildren<AssistantStepsProps>> = ({
    formulaId,
    selectedElement,
    alternateCreateError,
    setAlternateCreateError,
    handleIconClick,
    currentSelection,
    setIsVariationTab,
    setSelectedElement,
    removeSpacing,
    hasWriteAccess,
}) => {
    const SelectedElementKind = selectedElement?.kind;
    const isComponent = SelectedElementKind === ElementKind.variant;
    const variantType = selectedElement.variantType;

    const { data: componentTypes } = useComponentTypesQuery();

    const componentTypeData = useMemo(
        () => componentTypes?.map[variantType],
        [variantType, componentTypes],
    );

    return (
        <Container isComponent={isComponent} removeSpacing={!!removeSpacing}>
            <VariantDeletePrompt
                setSelectedElement={setSelectedElement}
                currentSelection={currentSelection}
                setIsVariationTab={setIsVariationTab}
            />
            {isComponent && formulaId ? (
                <Content>
                    <FormulaAlternateVariantDataView
                        headerName={componentTypeData?.name ?? ''}
                        childData={{
                            headerName: componentTypeData?.name ?? '',
                            kind: selectedElement.kind,
                            variantType: selectedElement.variantType,
                            id: selectedElement.elementId,
                        }}
                        formulaId={formulaId}
                        isEditable={hasWriteAccess}
                        alternateCreateError={alternateCreateError}
                        setAlternateCreateError={setAlternateCreateError}
                        selectedElement={selectedElement}
                        alternateVariantChangeIcon={
                            <StyledIcon size={40} onClick={handleIconClick} />
                        }
                    />
                </Content>
            ) : (
                <VariationPlaceholder />
            )}
        </Container>
    );
};

const Container = styled.div<StyledProps>(({ isComponent, removeSpacing }) => {
    return {
        position: 'relative',
        display: 'flex',
        gap: removeSpacing ? 0 : aampeTheme.spacing4(2),
        justifyContent: 'center',
        height: '100%',
        width: isComponent ? '100%' : '480px',
        overflowY: 'auto',
        flexDirection: 'column',
        backgroundColor: isComponent
            ? aampeTheme.colors.white
            : aampeTheme.colors.backgroundWG02,
        padding: aampeTheme.spacing4(0, 8),
        paddingBottom: isComponent ? 0 : aampeTheme.spacing4(6),
    };
});
const Content = styled.div({
    borderRight: `1px solid ${aampeTheme.colors.interactiveWG01}`,
    borderBottom: `1px solid ${aampeTheme.colors.interactiveWG01}`,
    borderTop: `1px solid ${aampeTheme.colors.interactiveWG01}`,
    width: '100%',
});

const StyledIcon = styled(ICONS.verticalMore)({
    cursor: 'pointer',
    padding: aampeTheme.spacing4(2),
    background: 'rgba(255, 255, 255, 0.7)',
    color: aampeTheme.colors.textAccessibleWG01,
    borderLeft: `1px solid ${aampeTheme.colors.interactiveWG01}`,
});

export default AssistantSteps;
